import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import moment from 'moment';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/*
  ChargingEnergyChart
  - Displays charging sessions over time as a bar chart
  - Supports metric toggle: Energy (kWh) vs Cost ($)
  - Supports aggregation: daily, weekly, monthly, yearly (default monthly)
  - Supports date range filtering and cumulative toggle
*/
const ChargingEnergyChart = ({ vehicleId, chartTitle = 'Charging', onRangeChange }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Controls
  const [aggregation, setAggregation] = useState('monthly');
  const [cumulative, setCumulative] = useState(false);
  const [stacked, setStacked] = useState(false);
  const [metric, setMetric] = useState(() => {
    try {
      const saved = window.localStorage.getItem('charging_metric');
      return saved === 'cost' ? 'cost' : 'kwh';
    } catch (e) {
      return 'kwh';
    }
  }); // 'kwh' | 'cost'
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    // Default to last 12 months for better visibility of recent history across year boundaries
    const lastYear = new Date(now.getFullYear() - 1, now.getMonth(), 1);
    return lastYear.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    // End of current month
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return nextMonth.toISOString().slice(0, 10);
  });

  // Fetch sessions
  useEffect(() => {
    const fetchSessions = async () => {
      if (!vehicleId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`/api/vehicles/${vehicleId}/charging/`);
        const rows = Array.isArray(res.data?.sessions) ? res.data.sessions : (Array.isArray(res.data) ? res.data : []);
        setSessions(rows);
      } catch (e) {
        console.error('Failed to load charging sessions', e);
        setError('Failed to load charging sessions');
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, [vehicleId]);

  // Emit range to parent if requested
  useEffect(() => {
    if (typeof onRangeChange === 'function') {
      onRangeChange(fromDate, toDate);
    }
  }, [fromDate, toDate, onRangeChange]);

  // Build aggregated series (split by type: Supercharger vs Wall Connector/Home)
  const { labels, superValues, homeValues } = useMemo(() => {
    if (loading) return { labels: [], superValues: [], homeValues: [] };

    const start = moment(fromDate);
    const end = moment(toDate);
    // Prepare label buckets
    const buckets = [];
    let cursor = start.clone().startOf(getPeriod(aggregation));
    const endBoundary = end.clone().endOf(getPeriod(aggregation));
    while (cursor.isSameOrBefore(endBoundary)) {
      buckets.push(cursor.clone());
      // advance by one period
      if (aggregation === 'daily') cursor.add(1, 'day');
      else if (aggregation === 'weekly') cursor.add(1, 'week');
      else if (aggregation === 'monthly') cursor.add(1, 'month');
      else if (aggregation === 'yearly') cursor.add(1, 'year');
      else cursor.add(1, 'month');
    }

    // Sum selected metric per bucket for each type
    const sumsSuper = new Array(buckets.length).fill(0);
    const sumsHome = new Array(buckets.length).fill(0);
    sessions.forEach(s => {
      const when = s.start_time || s.startTime || s.chargeStartDateTime;
      if (!when) return;
      const m = moment(when);
      if (!m.isValid()) return;
      if (m.isBefore(start) || m.isAfter(end)) return; // outside selected window

      // find bucket index by period start match
      const key = m.clone().startOf(getPeriod(aggregation)).format('YYYY-MM-DD');
      const idx = buckets.findIndex(b => b.format('YYYY-MM-DD') === key);
      if (idx < 0) return;

      const type = (s.charge_port_type || s.chargePortType || '').toString();
      const isSuper = /supercharger/i.test(type);
      const isHome = /home|wall\s*connector/i.test(type);

      let val = 0;
      if (metric === 'kwh') {
        const kwh = Number(s.energy_added_kwh ?? s.energyAddedKwh ?? 0) || 0;
        if (kwh <= 0) return;
        val = kwh;
      } else {
        const cost = (s.estimated_cost ?? s.total_cost);
        if (cost === null || cost === undefined) return;
        val = Number(cost) || 0;
        if (val <= 0) return;
      }

      if (isSuper) sumsSuper[idx] += val;
      else sumsHome[idx] += val; // Default everything else to 'Home' to ensure it shows up in chart
    });

    // cumulative if needed (independently per series)
    if (cumulative) {
      for (let i = 1; i < buckets.length; i++) {
        sumsSuper[i] += sumsSuper[i - 1];
        sumsHome[i] += sumsHome[i - 1];
      }
    }

    const labels = buckets.map(b => {
      if (aggregation === 'daily') return b.format('YYYY-MM-DD');
      if (aggregation === 'weekly') return `Wk ${b.isoWeek()} ${b.format('YYYY')}`;
      if (aggregation === 'yearly') return b.format('YYYY');
      return b.format('YYYY-MM'); // monthly default
    });

    return { labels, superValues: sumsSuper, homeValues: sumsHome };
  }, [sessions, fromDate, toDate, aggregation, cumulative, loading, metric]);

  const COLOR_SUPERCHARGER = '#ff4444'; // matches table tag for Supercharger
  const COLOR_WALL = '#2196F3'; // matches table tag color used for Wall Connector in table (blue)

  const data = useMemo(() => ({
    labels,
    datasets: [
      {
        label: 'Supercharger',
        data: superValues,
        backgroundColor: COLOR_SUPERCHARGER,
        ...(stacked ? { stack: 'all' } : {}),
      },
      {
        label: 'Wall Connector',
        data: homeValues,
        backgroundColor: COLOR_WALL,
        ...(stacked ? { stack: 'all' } : {}),
      }
    ]
  }), [labels, superValues, homeValues, metric, stacked]);

  // Clean up incoming chartTitle to avoid duplicated words like "Energy (kWh) Energy"
  const baseTitle = useMemo(() => {
    try {
      let t = String(chartTitle || '').trim();
      // Remove common suffixes the parent might include
      t = t.replace(/\s*Energy\s*\(kWh\)/ig, '')
           .replace(/\s*Energy\b/ig, '')
           .replace(/\s*Cost\s*\(\$\)/ig, '')
           .replace(/\s*\(kWh\)/ig, '')
           .replace(/\s*Cost\b/ig, '')
           .trim();
      if (!t) t = 'Charging';
      return t;
    } catch(e) {
      return 'Charging';
    }
  }, [chartTitle]);

  const options = useMemo(() => ({
    responsive: true,
    plugins: {
      legend: { position: 'top', labels: { color: '#e5e7eb' } },
      title: { display: true, text: metric === 'kwh' ? `${baseTitle} — ${cumulative ? 'Cumulative ' : ''}kWh` : `${baseTitle} — ${cumulative ? 'Cumulative ' : ''}$ Cost`, color: '#e5e7eb' },
      tooltip: { callbacks: { label: (ctx) => {
        const y = Number(ctx.parsed.y ?? 0);
        if (metric === 'kwh') return `${ctx.dataset.label}: ${y.toFixed(2)} kWh`;
        return `${ctx.dataset.label}: $${y.toFixed(2)}`;
      } } }
    },
    scales: {
      x: { ticks: { color: '#e5e7eb' }, grid: { color: '#1f2937' }, stacked: stacked },
      y: { beginAtZero: true, ticks: { color: '#e5e7eb', callback: (val) => metric === 'kwh' ? `${val}` : `$${val}` }, grid: { color: '#1f2937' }, stacked: stacked }
    }
  }), [baseTitle, metric, stacked, cumulative]);

  function getPeriod(agg) {
    if (agg === 'daily') return 'day';
    if (agg === 'weekly') return 'week';
    if (agg === 'yearly') return 'year';
    return 'month';
  }

  return (
    <div style={{ backgroundColor: '#0b1220', border: '1px solid #374151', borderRadius: 8, padding: 10 }}>
      {/* Header controls with left-aligned metric buttons and responsive wrapping */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6, rowGap: 6, marginBottom: 8 }}>
        {/* Left side: metric selector + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {/* Metric segmented control (smaller buttons) */}
          <div style={{ display: 'flex', border: '1px solid #374151', borderRadius: 6, overflow: 'hidden' }} role="group" aria-label="Charging metric selector">
            <button
              type="button"
              onClick={() => { setMetric('kwh'); try { window.localStorage.setItem('charging_metric', 'kwh'); } catch (e) {} }}
              style={{ padding: '4px 8px', fontSize: 12, lineHeight: 1.2, backgroundColor: metric === 'kwh' ? '#2563eb' : 'transparent', color: metric === 'kwh' ? '#fff' : '#e5e7eb', border: 'none', borderRight: '1px solid #374151', cursor: 'pointer' }}
            >
              kWh's
            </button>
            <button
              type="button"
              onClick={() => { setMetric('cost'); try { window.localStorage.setItem('charging_metric', 'cost'); } catch (e) {} }}
              style={{ padding: '4px 8px', fontSize: 12, lineHeight: 1.2, backgroundColor: metric === 'cost' ? '#2563eb' : 'transparent', color: metric === 'cost' ? '#fff' : '#e5e7eb', border: 'none', cursor: 'pointer' }}
            >
              $ Cost
            </button>
          </div>
          <div style={{ color: '#e5e7eb', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {metric === 'kwh' ? `${baseTitle} — kWh` : `${baseTitle} — $ Cost`}
          </div>
        </div>

        {/* Right side: filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ color: '#e5e7eb', whiteSpace: 'nowrap' }}>Aggregation:</label>
          <select className="form-select form-select-sm" value={aggregation} onChange={e => setAggregation(e.target.value)}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </select>
          <input className="form-control form-control-sm" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
          <input className="form-control form-control-sm" type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
          <label style={{ color: '#e5e7eb', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={cumulative} onChange={e => setCumulative(e.target.checked)} /> Cumulative
          </label>
          <label style={{ color: '#e5e7eb', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={stacked} onChange={e => setStacked(e.target.checked)} /> Stacked
          </label>
        </div>
      </div>

      {loading ? (
        <div style={{ color: '#9CA3AF', padding: '8px' }}>Loading charging data…</div>
      ) : error ? (
        <div style={{ color: '#f87171', padding: '8px' }}>{error}</div>
      ) : (
        <Bar data={data} options={options} />
      )}
    </div>
  );
};

export default ChargingEnergyChart;
